import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Pagination,
  Checkbox,
  Popover,
  TextField,
  Card,
  CardContent,
  IconButton,
  Collapse,
} from "@mui/material";
import Cookie from "js-cookie";
import { motion, AnimatePresence } from "framer-motion";
import { StyledButton } from "components/StyledComponents/styledComponents";
import CurrencyInput from "react-currency-input-field";
import { useMaterialUIController } from "context";
import { StyledSnackbar, StyledAlert } from "components/StyledComponents/styledComponents.js";
import { StyledCancelButton } from "components/StyledComponents/styledComponents";
import URLs from "constants/urls";
import ExtendedPopover from "./ExtendedInvPopover/extendInvPopover";
import PropTypes from "prop-types";

const ExtendedInvoice = ({ setAlert, bankAccounts }) => {
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const jwt_token = Cookie.get("jwt_token");
  const [entries, setEntries] = useState([]);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [newPercent, setNewPercent] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const rowsPerPage = 5;
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDay);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    setLoading(true);
    console.log("Button clicked ✅");
    console.log({ startDate, endDate, companyId: user.companyId, jwt_token });

    try {
      const response = await fetch(
        `${URLs.getEntriesForExtendedInvoice}?startDate=${startDate}&endDate=${endDate}&assignedCompanyId=${user.companyId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt_token ?? ""}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch entries");
      const data = await response.json();

      setEntries(data);
    } catch (error) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (entry) => {
    setSelectedEntries((prev) =>
      prev.find((e) => e.disburseId === entry.disburseId)
        ? prev.filter((e) => e.disburseId !== entry.disburseId)
        : [...prev, entry]
    );
  };

  const handleExpand = (disburseId) => {
    setExpandedRow(expandedRow === disburseId ? null : disburseId);
  };

  const handleProceed = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
  };

  const handleInvoiceClick = (invoiceNo) => {
    console.log(`View invoice: ${invoiceNo}`);
    // Implement view logic here
  };
  const handleProceedInv = async (payload) => {
    try {
      const res = await fetch(`${URLs.extendedPayout}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || "Request failed");
      }

      const responseText = await res.text();
      setAlert(responseText, "success");

      return responseText;
    } catch (error) {
      setAlert(error.message, "error");
      return null;
    }
  };

  useEffect(() => {
    if (newPercent && selectedEntries.length) {
      const totalDisbursed = selectedEntries.reduce((sum, entry) => sum + entry.disbursedAmount, 0);
      const amt = (totalDisbursed * newPercent) / 100;
      setNewAmount(amt.toFixed(2));
    } else if (newAmount && selectedEntries.length) {
      const totalDisbursed = selectedEntries.reduce((sum, entry) => sum + entry.disbursedAmount, 0);
      const pct = (newAmount / totalDisbursed) * 100;
      setNewPercent(pct.toFixed(2));
    }
  }, [newPercent, newAmount, selectedEntries]);

  // Pagination logic
  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return entries.slice(start, start + rowsPerPage);
  }, [entries, page]);

  const pageCount = Math.ceil(entries.length / rowsPerPage);

  const parseInvoices = (invoiceString) => {
    if (!invoiceString) return [];
    return invoiceString
      .split(";") // split by ;
      .map((inv) => inv.trim()) // remove spaces
      .filter((inv) => inv.length > 0); // remove empty strings
  };
  const getInvoiceNumber = (invoiceText) => {
    const match = invoiceText.match(/\d+/); // regex to get digits
    return match ? match[0] : null;
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        flexGrow: 1,
        display: "flex",
        mt: 1,
        p: 0,
        pt: 1,
        pb: 1,
        borderRadius: "8px",
        flexDirection: "column",
        backgroundColor: "#f8fafc", // Light gray background
      }}
    >
      <Box sx={{ width: "100%", p: 2 }}>
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          {/* Left: Start & End Date + Get Button */}
          <Grid item xs={12} sm="auto">
            <Grid container spacing={1} alignItems="center">
              <Grid item>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{ maxWidth: 200, width: "100%" }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{ maxWidth: 200, width: "100%" }}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item>
                <StyledButton onClick={fetchEntries}>Get</StyledButton>
              </Grid>
            </Grid>
          </Grid>

          {/* Right: Proceed Button */}
          <Grid item xs={12} sm="auto">
            <StyledButton onClick={handleProceed}>Proceed</StyledButton>
          </Grid>
        </Grid>
      </Box>
      {/* Header */}
      <Grid
        container
        sx={{
          backgroundColor: "#333333",
          color: "#fff",
          py: 1.5,
          px: 1,
          position: "sticky",
          top: 0,
          zIndex: 10,
          fontWeight: "bold",
          borderRadius: "8px 8px 0 0",
          fontSize: { xs: "0.75rem", sm: "0.85rem", md: "1rem" },
        }}
      >
        <Grid item xs={1} sx={{ display: { xs: "none", sm: "block" } }}>
          Select
        </Grid>
        <Grid item xs={3} sm={2}>
          App No
        </Grid>
        <Grid item xs={3} sm={2.5}>
          Customer
        </Grid>
        <Grid item xs={2} sx={{ display: { xs: "none", sm: "block" } }}>
          Provider
        </Grid>
        <Grid item xs={2} sx={{ display: { xs: "none", sm: "block" } }}>
          DSA
        </Grid>
        <Grid item xs={3} sm={2} sx={{ textAlign: "right" }}>
          Disbursed Amount
        </Grid>
      </Grid>

      {/* Rows */}
      <Paper
        elevation={3}
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          borderRadius: "0 0 8px 8px",
          overflow: "hidden",
        }}
      >
        <AnimatePresence>
          {paginatedEntries.map((entry) => (
            <motion.div
              key={entry.disburseId}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Grid
                container
                sx={{
                  borderBottom: "1px solid #eee",
                  py: 1,
                  alignItems: "center",
                  "&:hover": { backgroundColor: "#f5f5f5" },
                  fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                }}
              >
                <Grid item xs={1} sx={{ display: { xs: "none", sm: "block" } }}>
                  <Checkbox
                    checked={selectedEntries.some((e) => e.disburseId === entry.disburseId)}
                    onChange={() => handleSelect(entry)}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{
                      fontSize: "0.65rem",
                      minWidth: "60px",
                      px: 0.5,
                      py: 0.2,
                      textTransform: "none",
                      color: "violet",
                    }}
                    onClick={() => handleExpand(entry.disburseId)}
                  >
                    {expandedRow === entry.disburseId ? "Close" : "Expand"}
                  </Button>
                </Grid>
                <Grid item xs={3} sm={2}>
                  {entry.applicationNumber}
                </Grid>
                <Grid item xs={3} sm={2.5}>
                  {entry.customerName}
                </Grid>

                <Grid item xs={2} sx={{ display: { xs: "none", sm: "block" } }}>
                  {entry.providerName}
                </Grid>
                <Grid item xs={2} sx={{ display: { xs: "none", sm: "block" } }}>
                  {entry.dsaName}
                </Grid>
                <Grid item xs={3} sm={2} sx={{ textAlign: "right", fontWeight: "bold" }}>
                  ₹
                  {entry.disbursedAmount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Grid>
              </Grid>

              {/* Expandable Invoice Details */}
              <Collapse in={expandedRow === entry.disburseId}>
                <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
                  {/* Payout Summary in 2 Cards */}
                  <Grid container spacing={2} mb={2}>
                    {/* Card 1: Disbursed + Gross Payout */}
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography fontWeight="bold" fontSize="0.9rem" mb={1}>
                          Disbursement
                        </Typography>
                        <Typography fontSize="0.85rem">Disbursed Amount:</Typography>
                        <Typography fontSize="0.9rem" fontWeight="500" mb={1}>
                          ₹
                          {entry.disbursedAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>

                        <Typography fontSize="0.85rem">
                          Gross Payout ({entry.grossPayoutPercent}%):
                        </Typography>
                        <Typography fontSize="0.9rem" fontWeight="500">
                          ₹
                          {entry.grossPayoutAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Card>
                    </Grid>

                    {/* Card 2: DSA + Sub DSA Payout */}
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        <Typography fontWeight="bold" fontSize="0.9rem" mb={1}>
                          Payouts
                        </Typography>
                        <Typography fontSize="0.85rem">
                          DSA Payout ({entry.dsaPayoutPercent}%):
                        </Typography>
                        <Typography fontSize="0.9rem" fontWeight="500" mb={1}>
                          ₹
                          {entry.dsaPayoutAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>

                        <Typography fontSize="0.85rem">
                          Sub DSA Payout ({entry.subDSAPayoutPercent}%):
                        </Typography>
                        <Typography fontSize="0.9rem" fontWeight="500">
                          ₹
                          {entry.subDSAPaymentAmount.toLocaleString("en-IN", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          boxShadow: 2,
                          backgroundColor: "#fff",
                        }}
                      >
                        {parseInvoices(entry.invoices).map((inv, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              py: 1,
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <Typography fontSize={{ xs: "0.75rem", sm: "0.85rem" }}>
                              {inv}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{
                                fontSize: { xs: "0.7rem", sm: "0.8rem" },
                                color: "orange",
                              }}
                              onClick={() => {
                                const invoiceNo = getInvoiceNumber(inv);
                                handleInvoiceClick(invoiceNo); // API call with number only
                              }}
                            >
                              View
                            </Button>
                          </Box>
                        ))}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </motion.div>
          ))}
        </AnimatePresence>
      </Paper>

      {/* Pagination */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Pagination
          count={pageCount}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          size="small"
        />
      </Box>

      {/* Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorReference="anchorPosition"
        anchorPosition={{ top: window.innerHeight / 2, left: window.innerWidth / 2 }}
        transformOrigin={{ vertical: "center", horizontal: "center" }}
        PaperProps={{
          sx: {
            width: "85vw",
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: 4,
            overflow: "auto",
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            {/* Header with Close Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{
                  mb: 2,
                  pb: 1,
                  borderBottom: "3px solid #D4AF37",
                  display: "inline-block",
                }}
              >
                Raising Extended Invoice
              </Typography>

              <StyledCancelButton
                variant="outlined"
                color="error"
                size="small"
                onClick={handleClosePopover}
              >
                Close
              </StyledCancelButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              <ExtendedPopover
                selectedEntries={selectedEntries}
                bankAccounts={bankAccounts}
                onProceed={handleProceedInv}
                setAlert={setAlert}
              />
            </Box>
          </Box>
        </motion.div>
      </Popover>
    </Box>
  );
};

export default ExtendedInvoice;
ExtendedInvoice.propTypes = {
  bankAccounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  setAlert: PropTypes.func.isRequired,
};
